import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Tab,
  Tabs,
  ListGroup,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaRegClock, FaRegCheckCircle, FaShoppingBag } from "react-icons/fa";
import { orderAPI } from "../utils/api"; // API import 추가

const MyOrdersPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("accessToken") ? true : false;

  useEffect(() => {
    // 인증 확인
    if (!isAuthenticated) {
      navigate("/login", { state: { returnUrl: "/my-orders" } });
      return;
    }

    // API로 주문 데이터 가져오기
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = () => {
    setLoading(true);
    try {
      // API를 사용하여 주문 내역 가져오기
      orderAPI.getAll()
        .then(response => {
          // API 응답 구조에 맞게 데이터 추출
          const orders = response.data.results || [];
          
          console.log("주문 내역:", orders);
          
          setOrders(orders);
          
          // 로컬 스토리지에도 저장 (다른 페이지에서 사용할 수 있도록)
          localStorage.setItem("orders", JSON.stringify(orders));
          
          setLoading(false);
        })
        .catch(err => {
          console.error("API 주문 내역 로드 오류:", err);
          
          // API 호출 실패 시 로컬 스토리지 데이터 사용 (폴백)
          const savedOrders = JSON.parse(localStorage.getItem("orders") || "[]");
          
          // 최근 주문 추가 (localStorage의 latestOrder가 있으면)
          const latestOrder = JSON.parse(localStorage.getItem("latestOrder") || "null");
          
          if (latestOrder) {
            // 이미 orders 배열에 latestOrder의 receipt_id와 동일한 주문이 있는지 확인
            const orderExists = savedOrders.some(order => 
              order.receipt_id === latestOrder.receipt_id
            );
            
            if (!orderExists) {
              // 새 주문 추가
              const updatedOrders = [latestOrder, ...savedOrders];
              localStorage.setItem("orders", JSON.stringify(updatedOrders));
              setOrders(updatedOrders);
            } else {
              setOrders(savedOrders);
            }
          } else {
            setOrders(savedOrders);
          }
          
          setLoading(false);
        });
    } catch (err) {
      console.error("주문 내역 로드 오류:", err);
      setError("주문 내역을 불러오는데 실패했습니다.");
      setLoading(false);
    }
  };

  // 날짜 포맷 함수 - 시분초 포함
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleDateString('ko-KR', options);
  };

  // 금액 포맷 함수
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(amount);
  };

  // 주문 상태에 따른 뱃지 표시
  const getStatusBadge = (status) => {
    const statusMap = {
      DONE: { variant: "secondary", text: "기도 완료" },
      PROCESS: { variant: "primary", text: "사찰에서 기도 중" },
      Cancel: { variant: "danger", text: "기도 취소" },
      REJECT: { variant: "danger", text: "사찰에서 기도 거절 됨" },
      PREPARE: { variant: "warning", text: "사찰에서 기도 준비 중" },
      pending: { variant: "warning", text: "처리 중" },
      completed: { variant: "success", text: "완료" },
      cancelled: { variant: "danger", text: "취소" },
      refunded: { variant: "secondary", text: "환불" },
      default: { variant: "success", text: "완료" } // 기본값
    };
    
    const statusInfo = statusMap[status] || statusMap.default;
    
    return (
      <Badge bg={statusInfo.variant} pill>
        {statusInfo.text}
      </Badge>
    );
  };

  // 결제 수단 한글 표시
  const getPaymentMethodText = (method) => {
    const methodMap = {
      card: "신용카드",
      bank: "계좌이체",
      vbank: "가상계좌",
      phone: "휴대폰결제",
      kakao: "카카오페이",
      naverpay: "네이버페이",
      payco: "페이코",
      toss: "토스",
    };
    
    return methodMap[method] || method;
  };

  // 상품의 총 가격 계산 (옵션 포함)
  const getItemPrice = (item) => {
    if (!item) return 0;
    
    // API 응답 구조 처리
    if (item.item && item.options) {
      let price = item.item.price || 0;
      if (item.options && Array.isArray(item.options)) {
        for (const option of item.options) {
          price += option.option?.price || 0;
        }
      }
      return price;
    }
    
    // 기존 로컬 스토리지 구조 처리
    return item.price || 0;
  };

  // 탭에 따른 주문 필터링
  const getFilteredOrders = () => {
    if (activeTab === "all") return orders;
    
    const statusMap = {
      pending: orders.filter(order => 
        order.status === "pending" || order.status === "PREPARE"
      ),
      completed: orders.filter(order => 
        !order.status || 
        order.status === "completed" || 
        order.status === "DONE" || 
        order.status === "PROCESS"
      ),
      cancelled: orders.filter(order => 
        order.status === "cancelled" || 
        order.status === "Cancel" || 
        order.status === "REJECT" || 
        order.status === "refunded"
      ),
    };
    
    return statusMap[activeTab] || [];
  };

  // 주문 상세 페이지로 이동
  const handleViewOrderDetail = (orderId) => {
    navigate(`/my-orders/${orderId}`);
  };

  // 주문 취소 처리
  const handleCancelOrder = (orderId) => {
    if (!orderId) return;

    if (window.confirm("정말로 기도를 취소하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
      orderAPI.patch(orderId, { status: "Cancel" })
        .then(() => {
          alert("주문이 성공적으로 취소되었습니다.");
          fetchOrders();
        })
        .catch(err => {
          console.error("주문 취소 오류:", err);
          setError("주문 취소에 실패했습니다. 고객센터에 문의해주세요.");
        });
    }
  };

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h1 className="h2 fw-bold mb-4">나의 주문 내역</h1>
          <Button
            as={Link}
            to="/mypage"
            variant="link"
            className="p-0 mb-4 text-decoration-none"
            style={{ color: "var(--main-color)" }}
          >
            <FaArrowLeft className="me-2" /> 마이페이지로 돌아가기
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: "var(--main-color)" }}>
            <span className="visually-hidden">로딩 중...</span>
          </Spinner>
          <p className="mt-3">주문 내역을 불러오는 중입니다...</p>
        </div>
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
            style={{ borderBottom: "1px solid #dee2e6" }}
          >
            <Tab eventKey="all" title="전체 주문" />
            <Tab eventKey="pending" title="처리 중" />
            <Tab eventKey="completed" title="완료" />
            <Tab eventKey="cancelled" title="취소/환불" />
          </Tabs>

          {getFilteredOrders().length === 0 ? (
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="text-center py-5">
                <h3 className="h5 mb-4 text-muted">주문 내역이 없습니다.</h3>
                <Button
                  as={Link}
                  to="/pray"
                  variant="primary"
                  style={{
                    backgroundColor: "var(--main-color)",
                    borderColor: "var(--main-color)",
                    borderRadius: "100px",
                  }}
                >
                  기도 상품 보러가기
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <div>
              {getFilteredOrders().map((order, index) => (
                <Card key={index} className="shadow-sm border-0 rounded-4 mb-4">
                  <Card.Header className="bg-white py-3 border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="small text-muted">주문번호: </span>
                        <span className="fw-bold">{order.id || order.receipt_id || `ORDER_${index + 1}`}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaRegClock className="text-muted me-1" size={14} />
                        <small className="text-muted me-3">
                          {formatDate(order.date || order.orderedAt || new Date())}
                        </small>
                        {getStatusBadge(order.status)}
                      </div>
                    </div>
                  </Card.Header>
                  
                  <ListGroup variant="flush">
                    {(order.items || []).map((item, itemIndex) => (
                      <ListGroup.Item key={itemIndex} className="py-3 border-bottom">
                        <div className="d-flex">
                          {/* 상품 이미지 */}
                          {item.image || (item.item && item.item.image) ? (
                            <div
                              className="me-3"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                backgroundImage: `url(${item.image || (item.item && item.item.image)})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <div
                              className="me-3 bg-light d-flex align-items-center justify-content-center"
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "8px",
                                flexShrink: 0,
                                border: "1px solid #f0f0f0",
                              }}
                            >
                              <FaShoppingBag size={24} style={{ color: "var(--main-color)", opacity: 0.5 }} />
                            </div>
                          )}

                          {/* 상품 정보 */}
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h3 className="h6 mb-1">{item.item?.name || item.name}</h3>
                                <p className="text-muted small mb-2">
                                  {order.store?.name || item.templeName || ""}
                                </p>
                                
                                {/* 옵션 정보 */}
                                {item.options && item.options.length > 0 && (
                                  <div className="small text-muted mb-2">
                                    {item.options.map((option, i) => (
                                      <div key={i} className="ps-2">
                                        • {option.option?.name || option.name}
                                        {(option.option?.price || option.price) > 0 &&
                                          ` (+${formatCurrency(
                                            option.option?.price || option.price
                                          )}원)`}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                <p className="small mb-0">
                                  {formatCurrency(getItemPrice(item))}원 × {item.count || item.quantity || 1}
                                </p>
                              </div>
                              <div className="text-end">
                                <p
                                  className="mb-0 fw-bold"
                                  style={{ color: "var(--main-color)" }}
                                >
                                  {formatCurrency(getItemPrice(item) * (item.count || item.quantity || 1))}원
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  
                  <Card.Footer className="bg-white border-0 py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <span className="text-muted me-2">결제 방법:</span>
                        <span>{getPaymentMethodText(order.paymentMethod || "card")}</span>
                      </div>
                      <div className="text-end">
                        <span className="text-muted me-2">총 결제금액:</span>
                        <span className="h5 fw-bold mb-0" style={{ color: "var(--main-color)" }}>
                          {formatCurrency(order.price || order.total || 0)}원
                        </span>
                      </div>
                    </div>
                    
                    <div className="d-flex justify-content-end mt-3">
                      {/* 주문 상태별 버튼 */}
                      {order.status === "PREPARE" ? (
                        <div className="d-flex gap-2">
                          <Button
                            as={Link}
                            to={`/my-orders/${order.id || order.receipt_id || index}`}
                            variant="outline-primary"
                            className="px-3 py-2"
                            style={{
                              borderColor: "var(--main-color)",
                              color: "var(--main-color)",
                              borderRadius: "100px",
                              fontWeight: "500",
                            }}
                          >
                            기도 현황 보기
                          </Button>
                          <Button
                            variant="outline-danger"
                            className="px-3 py-2"
                            onClick={() => handleCancelOrder(order.id || order.receipt_id)}
                            style={{
                              borderColor: "#dc3545",
                              color: "#dc3545",
                              borderRadius: "100px",
                              fontWeight: "500",
                            }}
                          >
                            기도 취소하기
                          </Button>
                        </div>
                      ) : order.status === "DONE" ? (
                        <Button
                          as={Link}
                          to={`/my-orders/${order.id || order.receipt_id || index}`}
                          variant="primary"
                          className="px-4 py-2"
                          style={{
                            backgroundColor: "var(--main-color)",
                            borderColor: "var(--main-color)",
                            borderRadius: "100px",
                            fontWeight: "500",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          기도 사진 보기
                        </Button>
                      ) : order.status === "PROCESS" ? (
                        <Button
                          as={Link}
                          to={`/my-orders/${order.id || order.receipt_id || index}`}
                          variant="outline-primary"
                          className="px-4 py-2"
                          style={{
                            borderColor: "var(--main-color)",
                            color: "var(--main-color)",
                            borderRadius: "100px",
                            fontWeight: "500",
                          }}
                        >
                          기도 현황 보기
                        </Button>
                      ) : (
                        <Button
                          as={Link}
                          to={`/my-orders/${order.id || order.receipt_id || index}`}
                          variant="outline-primary"
                          style={{
                            borderColor: "var(--main-color)",
                            color: "var(--main-color)",
                            borderRadius: "100px",
                          }}
                        >
                          주문 상세보기
                        </Button>
                      )}
                    </div>
                  </Card.Footer>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default MyOrdersPage;